import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ArrowRight, Lock, Activity } from "lucide-react";
import Logo from "../components/gridvest/Logo";

// Background still uses a hosted image (see Background block below) so it is
// guaranteed to render in every browser. The wind-turbine sunset photo lives
// at /landing-bg.jpg in the public folder.

function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export default function ComingSoonPage() {
  const navigate = useNavigate();
  const now = useNow();

  const time = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "America/New_York",
  });

  return (
    <div
      className="relative flex min-h-screen flex-col overflow-hidden bg-[#050B14] text-[#F8FAFC]"
      data-testid="coming-soon-page"
    >
      {/* Background image (wind-turbine sunset) with cinematic ken-burns zoom */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "url('/landing-bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          animation: "iy-kenburns 36s ease-in-out infinite alternate",
          opacity: 0.52,
        }}
      />
      <style>{`
        @keyframes iy-kenburns {
          0%   { transform: scale(1.04) translate(0, 0); }
          100% { transform: scale(1.14) translate(-2%, -1.5%); }
        }
      `}</style>

      {/* Color & depth overlays */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(5,11,20,0.55) 0%, rgba(5,11,20,0.35) 35%, rgba(5,11,20,0.92) 100%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(800px 460px at 8% 100%, rgba(34,197,94,0.22), transparent 60%), radial-gradient(700px 460px at 96% 4%, rgba(56,189,248,0.12), transparent 60%), radial-gradient(900px 600px at 50% 50%, rgba(5,11,20,0.55), transparent 70%)",
        }}
      />
      {/* Vignette */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          boxShadow: "inset 0 0 220px 60px rgba(5,11,20,0.85)",
        }}
      />
      {/* Film grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Header */}
      <header className="relative z-10 mx-auto flex w-full max-w-[1320px] items-center justify-between px-6 pt-7 lg:px-12 lg:pt-9">
        <Logo size={32} />
        <div className="flex items-center gap-4">
          <span
            className="hidden items-center gap-2 rounded-full border border-[#1E293B] bg-[#0B1626]/70 px-3 py-1.5 font-body text-[10px] font-semibold uppercase tracking-[0.22em] text-[#22C55E] backdrop-blur-sm sm:inline-flex"
            data-testid="header-status-pill"
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22C55E] opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#22C55E]" />
            </span>
            By Invitation · Active
          </span>
          <span
            className="hidden items-center gap-2 font-body text-[10px] font-semibold uppercase tracking-[0.22em] text-[#64748B] md:inline-flex"
            data-testid="header-time"
          >
            <Activity className="h-3 w-3" />
            NY · {time}
          </span>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 mx-auto flex w-full max-w-[1320px] flex-1 flex-col items-stretch gap-12 px-6 pb-16 pt-14 lg:flex-row lg:items-center lg:gap-20 lg:px-12 lg:pt-20">
        <div className="flex-1">
          {/* Corner brackets */}
          <div className="relative">
            <span
              aria-hidden
              className="absolute -left-3 -top-3 h-4 w-4 border-l border-t border-[#22C55E]/60 sm:-left-4 sm:-top-4 sm:h-5 sm:w-5"
            />
            <span
              aria-hidden
              className="absolute -right-3 -top-3 h-4 w-4 border-r border-t border-[#22C55E]/60 sm:-right-4 sm:-top-4 sm:h-5 sm:w-5"
            />

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              <p className="font-body text-[10px] font-semibold uppercase tracking-[0.32em] text-[#22C55E]">
                Infra · Operations · Intelligence
              </p>

              <h1
                className="mt-5 font-display text-[2.6rem] font-bold leading-[1.02] tracking-[-0.025em] text-[#F8FAFC] sm:text-6xl lg:text-[5rem]"
                data-testid="hero-title"
              >
                Infrastructure
                <br />
                <span className="bg-gradient-to-r from-[#22C55E] via-[#10B981] to-[#0EA5E9] bg-clip-text text-transparent">
                  Intelligence
                </span>{" "}
                Starts Here.
              </h1>

              <p
                className="mt-7 max-w-2xl font-body text-base leading-[1.65] text-[#CBD5E1] sm:text-[1.05rem]"
                data-testid="hero-subtitle"
              >
                Helping owners, municipalities, and operators organize
                infrastructure agreements, track critical dates, and improve
                operational continuity.
              </p>
            </motion.div>
          </div>

          {/* Asset class chips */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3"
          >
            <span className="inline-flex items-center gap-2 font-body text-[11px] font-semibold uppercase tracking-[0.2em] text-[#94A3B8]">
              <Lock className="h-3.5 w-3.5 text-[#22C55E]" />
              By Invitation
            </span>
            {["Towers", "Fiber", "Billboards", "Data Centers", "Renewables", "Substations"].map(
              (cls) => (
                <span
                  key={cls}
                  className="relative font-body text-[11px] font-semibold uppercase tracking-[0.2em] text-[#64748B] before:absolute before:-left-3.5 before:top-1/2 before:h-1 before:w-1 before:-translate-y-1/2 before:rounded-full before:bg-[#1E293B]"
                >
                  {cls}
                </span>
              )
            )}
          </motion.div>
        </div>

        {/* CTA card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
          className="w-full max-w-md self-stretch lg:w-[440px]"
        >
          <div className="relative overflow-hidden rounded-2xl border border-[#1E293B] bg-[#0B1626]/85 p-8 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.85)] backdrop-blur-md">
            {/* Inner highlight */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#22C55E]/40 to-transparent"
            />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-body text-[10px] font-semibold uppercase tracking-[0.28em] text-[#22C55E]">
                  Private Engagement
                </p>
                <h2
                  className="mt-2 font-display text-2xl font-semibold leading-tight text-[#F8FAFC]"
                  data-testid="cta-title"
                >
                  Request a Private
                  <br />
                  Walkthrough
                </h2>
              </div>
              <span className="rounded-full border border-[#1E293B] px-2.5 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.22em] text-[#64748B]">
                IY · 2026
              </span>
            </div>

            <p className="mt-5 font-body text-sm leading-relaxed text-[#94A3B8]">
              A 10-minute no-pressure overview tailored to your portfolio.
              See how InfraYield organizes agreements, surfaces critical
              dates, and reduces operational risk.
            </p>

            <button
              type="button"
              onClick={() => navigate("/walkthrough")}
              data-testid="cta-walkthrough-button"
              className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#22C55E] to-[#10B981] px-5 py-3.5 font-display text-sm font-semibold text-[#07111F] shadow-[0_10px_40px_-10px_rgba(34,197,94,0.55)] transition-transform hover:translate-y-[-1px]"
            >
              Request a Private Walkthrough
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>

            <div className="mt-5 flex items-center gap-3 border-t border-[#1E293B] pt-4">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#1E293B] bg-[#07111F] font-display text-[10px] font-bold text-[#22C55E]">
                NDA
              </span>
              <p className="font-body text-[11px] leading-relaxed text-[#64748B]">
                Vetted owners, municipalities, and operators only. We
                respond within one business day.
              </p>
            </div>
          </div>

          {/* Bottom helper */}
          <p className="mt-4 font-body text-[10px] uppercase tracking-[0.22em] text-[#475569]">
            No public sign-ups · No spam · Discreet outreach
          </p>
        </motion.div>
      </main>

      {/* Pillars — why infrastructure intelligence matters */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.35, ease: "easeOut" }}
        className="relative z-10 mx-auto w-full max-w-[1320px] px-6 lg:px-12"
      >
        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-[#1E293B] bg-[#1E293B]/60 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              value: "10–50+",
              unit: "Years",
              label: "Typical infrastructure agreements can last decades.",
            },
            {
              value: "1",
              unit: "Missed Deadline",
              label:
                "One missed renewal, escalation, or notice period can create costly consequences.",
            },
            {
              value: "100",
              unit: "% Visibility",
              label:
                "One place to organize infrastructure agreements, amendments, and critical dates.",
            },
            {
              value: "3",
              unit: "Core Audiences",
              label:
                "Built for Owners, Municipalities, and Infrastructure Operators.",
            },
          ].map((p, idx) => (
            <div
              key={p.unit}
              className="flex flex-col bg-[#070F1B]/95 p-6 backdrop-blur-md"
              data-testid={`pillar-${idx}`}
            >
              <p className="font-display text-3xl font-bold leading-none tracking-tight text-[#F8FAFC] sm:text-4xl">
                {p.value}
              </p>
              <p className="mt-2 font-body text-[11px] font-semibold uppercase tracking-[0.22em] text-[#22C55E]">
                {p.unit}
              </p>
              <p className="mt-3 font-body text-sm leading-relaxed text-[#94A3B8]">
                {p.label}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Footer */}
      <footer className="relative z-10 mx-auto mt-8 w-full max-w-[1320px] px-6 pb-7 lg:px-12">
        <div className="flex flex-col items-start gap-3 border-t border-[#1E293B]/60 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <p
              className="font-body text-[11px] text-[#64748B]"
              data-testid="footer-copyright"
            >
              © {new Date().getFullYear()} InfraYield, Inc.
            </p>
            <span className="hidden font-body text-[10px] uppercase tracking-[0.22em] text-[#1E293B] sm:inline">
              · SYS:ONLINE
            </span>
          </div>
          <a
            href="/overview"
            className="font-body text-[11px] font-semibold uppercase tracking-[0.28em] text-[#475569] transition-colors hover:text-[#94A3B8]"
            data-testid="footer-coming-soon"
            aria-label="Coming Soon"
          >
            Coming Soon
          </a>
        </div>
        {/* Discreet internal links — nearly invisible, only visible on hover */}
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1">
          <a
            href="/pilot"
            className="font-body text-[10px] uppercase tracking-[0.2em] text-[#0F1B2D] transition-colors hover:text-[#475569]"
            data-testid="footer-pilot-link"
          >
            Pilot
          </a>
          <a
            href="/privacy"
            className="font-body text-[10px] uppercase tracking-[0.2em] text-[#0F1B2D] transition-colors hover:text-[#475569]"
          >
            Privacy
          </a>
          <a
            href="/terms"
            className="font-body text-[10px] uppercase tracking-[0.2em] text-[#0F1B2D] transition-colors hover:text-[#475569]"
          >
            Terms
          </a>
        </div>
      </footer>

      <Toaster theme="dark" position="top-right" richColors closeButton />
    </div>
  );
}
